// ==========================================
// 📄 Blog Page Pagination & Breadcrumb Memory
// ==========================================

const ITEMS_PER_PAGE = 9;
const STORAGE_KEY = "blogs_selected_page";

function initBlogPagination() {
  const blogsGrid = document.querySelector(".blogs-grid");
  if (!blogsGrid) {
    // If not on blogs listing page, initialize breadcrumb memory on single blog page
    initBlogBreadcrumb();
    return;
  }

  const cards = Array.from(blogsGrid.querySelectorAll(".blog-card"));
  const totalItems = cards.length;
  if (totalItems <= ITEMS_PER_PAGE) return; // No pagination needed if 9 or fewer

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Look for or create pagination container
  let paginationContainer = document.getElementById("blogsPagination");
  if (!paginationContainer) {
    paginationContainer = document.createElement("div");
    paginationContainer.id = "blogsPagination";
    paginationContainer.className = "blogs-pagination";
    blogsGrid.insertAdjacentElement("afterend", paginationContainer);
  }

  // Get current page from URL (?page=X) or default to 1
  function getInitialPage() {
    const params = new URLSearchParams(window.location.search);
    const pageParam = parseInt(params.get("page"), 10);
    if (!isNaN(pageParam) && pageParam >= 1 && pageParam <= totalPages) {
      return pageParam;
    }
    return 1;
  }

  let currentPage = getInitialPage();

  // Attach from_page parameter to all blog cards so single blog pages know the return page
  function updateCardLinks(page) {
    const links = blogsGrid.querySelectorAll(".blog-card a");
    links.forEach((a) => {
      try {
        const dest = new URL(a.href, window.location.origin);
        if (dest.origin === window.location.origin) {
          if (page > 1) {
            dest.searchParams.set("from_page", page.toString());
          } else {
            dest.searchParams.delete("from_page");
          }
          a.href = dest.toString();
        }
      } catch (err) {}
    });
  }

  function renderPage(page, scrollToTop = false) {
    currentPage = Math.max(1, Math.min(page, totalPages));

    // Save selected page in sessionStorage
    try {
      sessionStorage.setItem(STORAGE_KEY, currentPage.toString());
    } catch (e) {}

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    // Show / hide cards with smooth fade-in
    cards.forEach((card, index) => {
      if (index >= startIndex && index < endIndex) {
        card.classList.remove("is-hidden");
        card.classList.remove("fade-in");
        // Force reflow to restart CSS animation
        void card.offsetWidth;
        card.classList.add("fade-in");
      } else {
        card.classList.add("is-hidden");
        card.classList.remove("fade-in");
      }
    });

    // Update blog card links with current page
    updateCardLinks(currentPage);

    // Build pagination controls
    renderControls();

    // Update URL query param without reload
    const url = new URL(window.location.href);
    if (currentPage === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", currentPage);
    }
    window.history.replaceState({ page: currentPage }, "", url.toString());

    // Scroll to top of blogs section
    if (scrollToTop) {
      const section = document.querySelector(".blogs-section");
      if (section) {
        const headerOffset = 90;
        const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: sectionPosition - headerOffset,
          behavior: "smooth",
        });
      }
    }
  }

  function renderControls() {
    paginationContainer.innerHTML = "";

    // 1. Previous Button
    const prevBtn = document.createElement("button");
    prevBtn.type = "button";
    prevBtn.className = `pagination-btn prev-btn ${currentPage === 1 ? "disabled" : ""}`;
    prevBtn.innerHTML = '<i class="fa-solid fa-angle-left"></i> <span>Prev</span>';
    prevBtn.setAttribute("aria-label", "Previous page");
    if (currentPage > 1) {
      prevBtn.addEventListener("click", () => renderPage(currentPage - 1, true));
    }
    paginationContainer.appendChild(prevBtn);

    // 2. Page Number Buttons (smart range with ellipsis if many pages)
    const pageNumbers = getPageRange(currentPage, totalPages);

    pageNumbers.forEach((p) => {
      if (p === "...") {
        const dots = document.createElement("span");
        dots.className = "pagination-btn dots";
        dots.textContent = "...";
        paginationContainer.appendChild(dots);
      } else {
        const numBtn = document.createElement("button");
        numBtn.type = "button";
        numBtn.className = `pagination-btn page-num ${p === currentPage ? "active" : ""}`;
        numBtn.textContent = p;
        numBtn.setAttribute("aria-label", `Page ${p}`);
        if (p !== currentPage) {
          numBtn.addEventListener("click", () => renderPage(p, true));
        }
        paginationContainer.appendChild(numBtn);
      }
    });

    // 3. Next Button
    const nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = `pagination-btn next-btn ${currentPage === totalPages ? "disabled" : ""}`;
    nextBtn.innerHTML = '<span>Next</span> <i class="fa-solid fa-angle-right"></i>';
    nextBtn.setAttribute("aria-label", "Next page");
    if (currentPage < totalPages) {
      nextBtn.addEventListener("click", () => renderPage(currentPage + 1, true));
    }
    paginationContainer.appendChild(nextBtn);

    // 4. Info summary (e.g., "Showing 1–9 of 46 articles")
    const startNum = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endNum = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
    const info = document.createElement("div");
    info.className = "pagination-info";
    info.textContent = `Showing ${startNum}${startNum === endNum ? "" : `–${endNum}`} of ${totalItems} articles (Page ${currentPage} of ${totalPages})`;
    paginationContainer.appendChild(info);
  }

  function getPageRange(current, total) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
      return [1, 2, 3, 4, 5, "...", total];
    }
    if (current >= total - 3) {
      return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, "...", current - 1, current, current + 1, "...", total];
  }

  // Intercept click on any blog card to guarantee state persistence
  blogsGrid.addEventListener("click", (e) => {
    const link = e.target.closest("a");
    if (!link) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, currentPage.toString());
      if (currentPage > 1) {
        const dest = new URL(link.href, window.location.origin);
        if (dest.origin === window.location.origin && !dest.searchParams.has("from_page")) {
          dest.searchParams.set("from_page", currentPage.toString());
          link.href = dest.toString();
        }
      }
    } catch (err) {}
  });

  // Handle browser Back/Forward navigation
  window.addEventListener("popstate", () => {
    const newPage = getInitialPage();
    if (newPage !== currentPage) {
      renderPage(newPage, false);
    }
  });

  // Initial render
  renderPage(currentPage, false);
}

// ==========================================
// 🔗 Breadcrumb Return Memory for Single Blog Pages
// ==========================================
function initBlogBreadcrumb() {
  const breadcrumbLinks = document.querySelectorAll(
    '.blogs-heading a, .breadcrumb a[href*="blogs.html"], a.blog-breadcrumb'
  );
  if (!breadcrumbLinks.length) return;

  let pageNumber = null;

  // 1. Check URL query param (?from_page=X)
  const urlParams = new URLSearchParams(window.location.search);
  const fromPageParam = parseInt(urlParams.get("from_page"), 10);
  if (!isNaN(fromPageParam) && fromPageParam > 1) {
    pageNumber = fromPageParam;
  }

  // 2. Check sessionStorage
  if (!pageNumber) {
    try {
      const stored = parseInt(sessionStorage.getItem(STORAGE_KEY), 10);
      if (!isNaN(stored) && stored > 1) {
        pageNumber = stored;
      }
    } catch (e) {}
  }

  // 3. Fallback: Check document.referrer (e.g., https://cognospace.in/blogs.html?page=2)
  if (!pageNumber && document.referrer) {
    try {
      const refUrl = new URL(document.referrer);
      const refPage = parseInt(refUrl.searchParams.get("page"), 10);
      if (!isNaN(refPage) && refPage > 1) {
        pageNumber = refPage;
      }
    } catch (e) {}
  }

  // If previous page was > 1, update breadcrumb target
  if (pageNumber && pageNumber > 1) {
    breadcrumbLinks.forEach((link) => {
      const currentHref = link.getAttribute("href") || "";
      if (currentHref.includes("blogs.html") || currentHref === "/blogs" || currentHref === "blogs") {
        const baseHref = currentHref.split("?")[0];
        const targetUrl = `${baseHref}?page=${pageNumber}`;
        link.setAttribute("href", targetUrl);

        link.addEventListener("click", (e) => {
          e.preventDefault();
          window.location.href = targetUrl;
        });
      }
    });
  }
}

// Auto-initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBlogPagination);
} else {
  initBlogPagination();
}

export { initBlogPagination, initBlogBreadcrumb };

